/**
 * สร้างกลุ่มย่อยจากหมวดหวยต่างประเทศแบบ Dynamic
 * @param {Array<Object>} lotteries - array ของ lottery (เฉพาะที่ category_id = 2)
 * @returns {Array<{title:string, prefixes:string[]}>}
 */
export function buildForeignSubgroups(lotteries) {
  if (!Array.isArray(lotteries)) return [];

  const groups = {};
  lotteries.forEach((row) => {
    const code = row?.code || "";
    if (!code) return;
    const prefix = code.slice(0, 3); // ใช้ 3 ตัวแรก
    if (!groups[prefix]) {
      groups[prefix] = {
        title: row?.name || prefix, // ตั้งชื่อกลุ่มเป็น name ตัวแรก หรือ prefix
        prefixes: [prefix],
      };
    }
  });

  return Object.values(groups);
}

/**
 * ดึง prefix สำหรับจัดกลุ่มย่อย "หวยต่างประเทศ"
 * - ใช้ 4 ตัวแรกของ lottery.code
 */
export function getCodePrefix4(code) {
  return (code || "").slice(0, 3);
}

/**
 * เติมเมตา group ให้ "กลุ่มต่างประเทศ":
 * - group_name = ชื่อของรายการแรกในกลุ่ม
 * - group_code = 3 ตัวแรกของ code ของรายการแรกในกลุ่ม
 */
export function withForeignGroupMeta(groupObj) {
  const first =
    Array.isArray(groupObj.lottery) && groupObj.lottery.length > 0
      ? groupObj.lottery[0]
      : null;

  return {
    ...groupObj,
    group_name: first?.name || "",
    group_code: getCodePrefix4(first?.code),
  };
}

/**
 * เติมเมตา group ให้ "หมวดอื่น ๆ (ไทย/หุ้น/ยี่กี)":
 * - group_name = category_name
 * - group_code = ตาม mapping THAI/SET/YIKI
 */
const TOP_GROUP_CODE_BY_ID = {
  1: "THAI", // หวยไทย
  3: "SET",  // หวยหุ้น
  4: "YIKI", // หวยยี่กี
};

export function withNonForeignGroupMeta(groupObj) {
  const code =
    TOP_GROUP_CODE_BY_ID?.[groupObj?.category_id] ||
    (groupObj?.category_name ? groupObj.category_name.toUpperCase() : "GEN");

  return {
    ...groupObj,
    group_name: groupObj?.category_name || "",
    group_code: code,
  };
}

/**
 * จัดกลุ่มข้อมูลทั้งหมด:
 * - หมวดอื่น ๆ (ไทย/หุ้น/ยี่กี) = คงเดิม 1 กลุ่มต่อหมวด + เติม group_name/group_code (THAI/SET/YIKI)
 * - เฉพาะ "หวยต่างประเทศ" (category_id = 2) = แยกหลายกลุ่มย่อยตาม prefix(4)
 *   โดยใช้ชื่อกลุ่ม(category_name) = ชื่อของรายการแรกในกลุ่มย่อยนั้น
 */
export function buildGroupedLotteriesDynamic(data) {
  if (!Array.isArray(data)) return [];

  const result = [];

  data.forEach((cat) => {
    const isForeign = cat?.category_id === 2;

    if (isForeign) {
      // ---- จัดกลุ่มเฉพาะ "หวยต่างประเทศ" ตาม prefix(4) ----
      const buckets = {}; // { prefix4: [rows...] }
      (cat?.lottery || []).forEach((row) => {
        const p4 = getCodePrefix4(row?.code);
        if (!p4) return;
        if (!buckets[p4]) buckets[p4] = [];
        buckets[p4].push(row);
      });

      Object.keys(buckets).forEach((p4) => {
        const lot = buckets[p4];
        const firstName = lot?.[0]?.name || cat?.category_name || "หวยต่างประเทศ";

        const group = {
          category_id: cat?.category_id, // 2
          category_at: cat?.category_at,
          category_name: firstName, // ใช้ชื่อของ row แรกเป็นชื่อกลุ่มย่อย
          lottery: lot,
        };

        result.push(withForeignGroupMeta(group));
      });
      return;
    }

    // ---- หมวดอื่น ๆ (ไทย/หุ้น/ยี่กี) → คงเดิมทั้งหมดในหมวดเดียว + meta THAI/SET/YIKI ----
    const group = {
      category_id: cat?.category_id,
      category_at: cat?.category_at,
      category_name: cat?.category_name,
      lottery: Array.isArray(cat?.lottery) ? cat.lottery.slice() : [],
    };
    result.push(withNonForeignGroupMeta(group));
  });

  return result;
}
